import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { formatCurrency } from "@/lib/format";
import { startOfYear } from "date-fns";
import { DateField, fmtISO } from "./_shared";
import DrillDownModal from "@/components/DrillDownModal";
import { Button } from "@/components/ui/button";
import { TrendingUp, TrendingDown } from "lucide-react";

export function EquityChangesReport({ currency }) {
  const [start, setStart] = useState(startOfYear(new Date()));
  const [end, setEnd] = useState(new Date());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [equityCodes, setEquityCodes] = useState({ contrib: [], draws: [] });
  const [drill, setDrill] = useState(null);
  // Net income drill toggles between income/expense buckets — the report
  // rolls both up so we expose two side-buttons in the modal footer via
  // reopening with a different kind. Simplest UX: two links on the row.

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/accounting/equity-changes", {
        params: { start: fmtISO(start), end: fmtISO(end) },
      });
      setData(data);
    } finally { setLoading(false); }
  }, [start, end]);
  useEffect(() => { load(); }, [load]);

  // Fetch equity CoA once so we can drill contributions/draws — the backend
  // classifies by name ("draw" ⇒ draws, else contributions).
  useEffect(() => {
    (async () => {
      try {
        const { data: coa } = await api.get("/coa");
        const equity = (coa || []).filter((a) => (a.type || "").toLowerCase() === "equity");
        const contrib = equity.filter((a) => !/draw/i.test(a.name || "")).map((a) => a.code);
        const draws = equity.filter((a) => /draw/i.test(a.name || "")).map((a) => a.code);
        setEquityCodes({ contrib, draws });
      } catch (e) { /* non-blocking */ }
    })();
  }, []);

  const drillContrib = () => {
    if (!equityCodes.contrib.length) return;
    setDrill({
      title: `Owner contributions · ${data.start} → ${data.end}`,
      params: { kind: "je_account", account_codes: equityCodes.contrib.join(","),
                start: fmtISO(start), end: fmtISO(end) },
    });
  };
  const drillDraws = () => {
    if (!equityCodes.draws.length) return;
    setDrill({
      title: `Owner draws · ${data.start} → ${data.end}`,
      params: { kind: "je_account", account_codes: equityCodes.draws.join(","),
                start: fmtISO(start), end: fmtISO(end) },
    });
  };
  const drillNet = (kind) => setDrill({
    title: `${kind === "income" ? "Income" : "Expenses"} for period · Net income drill`,
    params: { kind, start: fmtISO(start), end: fmtISO(end) },
  });

  return (
    <div data-testid="equity-report" className="space-y-6">
      <div className="flex flex-wrap items-end gap-3">
        <DateField label="From" value={start} onChange={setStart} testId="eq-start" />
        <DateField label="To" value={end} onChange={setEnd} testId="eq-end" />
      </div>
      {loading && !data && <div className="text-sm text-[var(--muted-text)]">Loading…</div>}
      {data && (
        <>
          <div className="rounded-md border border-[var(--divider)] bg-[#FFF9F0] px-3 py-2 text-xs text-[#7A6C4E]" data-testid="eq-currency-note">
            All figures below are a <strong>native sum in {data.currency || "USD"}</strong>. This report does not apply live FX conversion — for per-rail views, see the P&amp;L or Cash Flow reports.
          </div>
        <div className="card-flat overflow-hidden">
          <div className="px-5 py-3 border-b border-[var(--divider)] text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">
            Statement of Changes in Equity · {data.start} → {data.end}
          </div>
          <table className="w-full text-sm">
            <tbody>
              <EqRow label="Opening retained earnings" value={data.opening_retained_earnings}
                     currency={data.currency || currency} testId="eq-opening" />
              <EqRow label="+ Owner contributions" value={data.contributions}
                     currency={data.currency || currency} testId="eq-contrib"
                     onDrill={equityCodes.contrib.length ? drillContrib : null} />
              <tr className="border-b border-[var(--divider)]">
                <td className="py-2.5 px-5">+ Net income for period</td>
                <td className="py-2.5 px-5 text-right font-mono-data" data-testid="eq-net-income">
                  <span className="inline-flex items-center gap-2">
                    <span>{formatCurrency(data.net_income, data.currency || currency)}</span>
                    <span className="text-[10px] text-[var(--muted-text)]">drill:</span>
                    <button data-testid="eq-drill-income" onClick={() => drillNet("income")}
                            className="text-[10px] inline-flex items-center gap-0.5 text-[#2F6B4A] hover:underline">
                      <TrendingUp className="w-3 h-3" /> income
                    </button>
                    <button data-testid="eq-drill-expense" onClick={() => drillNet("expense")}
                            className="text-[10px] inline-flex items-center gap-0.5 text-[#B4283A] hover:underline">
                      <TrendingDown className="w-3 h-3" /> expense
                    </button>
                  </span>
                </td>
              </tr>
              <EqRow label="− Owner draws" value={data.draws}
                     currency={data.currency || currency} testId="eq-draws" negative
                     onDrill={equityCodes.draws.length ? drillDraws : null} />
              <tr className="border-t-2 border-[#2F4F4F] font-semibold bg-[var(--hover)]">
                <td className="py-3 px-5">Closing retained earnings</td>
                <td className="py-3 px-5 text-right font-mono-data" data-testid="eq-closing-re">{formatCurrency(data.closing_retained_earnings, data?.currency || currency)}</td>
              </tr>
              <tr className="border-t border-[var(--divider)] font-semibold">
                <td className="py-3 px-5">Total closing equity</td>
                <td className="py-3 px-5 text-right font-mono-data" data-testid="eq-closing-equity">{formatCurrency(data.closing_equity, data?.currency || currency)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        </>
      )}
      <DrillDownModal
        open={!!drill}
        onClose={() => setDrill(null)}
        title={drill?.title}
        params={drill?.params}
        currency={data?.currency || currency}
      />
    </div>
  );
}

function EqRow({ label, value, currency, testId, negative, onDrill }) {
  return (
    <tr
      className={`border-b border-[var(--divider)] ${onDrill ? "cursor-pointer hover:bg-[var(--hover)]" : ""}`}
      onClick={onDrill || undefined}
      title={onDrill ? "Click to drill into journal-entry detail" : undefined}
    >
      <td className="py-2.5 px-5">
        {label}
        {onDrill && <span className="ml-1.5 text-[10px] text-[var(--muted-text)]">↗</span>}
      </td>
      <td className={`py-2.5 px-5 text-right font-mono-data ${negative ? "text-[#B4283A]" : ""}`} data-testid={testId}>
        {formatCurrency(value, currency)}
      </td>
    </tr>
  );
}
